/**
 * Helpers for walking the server-driven menu tree stored in `localStorage['userAssignedMenus']`.
 *
 * Both LoginComponent and MenuService previously assumed the FIRST top-level menu always has a
 * `subMenus` array, and read `sortedMenus[0].subMenus[0].menuUrl` directly. That throws a
 * TypeError whenever the first top-level entry is a leaf (a menu with its own `menuUrl` and no
 * children) — which is exactly the shape of the Assisted eKYC tree, where "Home" comes first.
 *
 * `findFirstMenuUrl` walks the tree depth-first, honouring `position`, and returns the first node
 * that actually has somewhere to navigate to.
 */

export interface AssignedMenuNode {
  menuName?: string;
  menuUrl?: string;
  position?: number;
  subMenus?: AssignedMenuNode[] | null;
}

function byPosition(a: AssignedMenuNode, b: AssignedMenuNode): number {
  return (a?.position ?? 0) - (b?.position ?? 0);
}

/** Depth-first search for the first navigable menu URL. Returns null when the tree has none. */
export function findFirstMenuUrl(menus: AssignedMenuNode[] | null | undefined): string | null {
  if (!Array.isArray(menus) || menus.length === 0) {
    return null;
  }

  for (const menu of [...menus].sort(byPosition)) {
    if (menu?.menuUrl) {
      return menu.menuUrl;
    }
    const nested = findFirstMenuUrl(menu?.subMenus ?? null);
    if (nested) {
      return nested;
    }
  }

  return null;
}

/** Reads and parses `localStorage['userAssignedMenus']`, tolerating absent or corrupt values. */
export function readAssignedMenus(): AssignedMenuNode[] | null {
  const raw = localStorage.getItem('userAssignedMenus');
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AssignedMenuNode[]) : null;
  } catch {
    return null;
  }
}
